import { makeAutoObservable, reaction } from 'mobx'

import { addMonths } from 'date-fns/addMonths'
import { endOfMonth } from 'date-fns/endOfMonth'
import { getMonth } from 'date-fns/getMonth'
import { startOfMonth } from 'date-fns/startOfMonth'

import { Account } from '../types'

import type { GlobalStore } from './GlobalStore'

export class GlobalUiStore {
  globalStore: GlobalStore

  accountFilterMap: Map<string, boolean> = new Map()

  displayedDate = new Date()
  displayedDateRange = {
    start: startOfMonth(this.displayedDate),
    end: endOfMonth(this.displayedDate),
  }

  constructor(globalStore: GlobalStore) {
    makeAutoObservable(this)
    this.globalStore = globalStore

    reaction(
      () => this.globalStore.accountStore.visibleAccounts,
      (visibleAccounts) => {
        this.populateAccountFilterMap(visibleAccounts)
      },
    )
  }

  get allSelectedAccounts() {
    return this.globalStore.accountStore.detailedAccounts.filter(
      (account: Account) => !account.is_excluded_from_accounts,
    )
  }

  get selectedAccounts() {
    return this.globalStore.accountStore.visibleAccounts.filter(
      (account: Account) => !account.is_excluded_from_accounts,
    )
  }

  get selectedAccountGuids() {
    return this.selectedAccounts.map((account) => account.guid)
  }

  get isCopyLoaded() {
    return this.globalStore.copyStore.isInitialized
  }

  get isConfigLoaded() {
    return this.globalStore.appDataStore.isAppDataLoaded
  }

  get areUserFeaturesLoaded() {
    return this.globalStore.userStore.userFeaturesLoaded
  }

  get isInitialized() {
    return this.isCopyLoaded && this.isConfigLoaded && this.areUserFeaturesLoaded
  }

  populateAccountFilterMap = (accounts: Account[]) => {
    if (accounts) {
      accounts.forEach((account) => {
        this.accountFilterMap.set(account.guid, !account.is_excluded_from_accounts)
      })
    }
  }

  resetAccountFilterMap = () => {
    this.accountFilterMap.clear()
    this.populateAccountFilterMap(this.globalStore.accountStore.visibleAccounts)
  }

  updateAccountFilterMap = (guid: string, isIncluded: boolean) => {
    this.accountFilterMap.set(guid, isIncluded)
  }

  setDisplayedDate = (startDate: Date, endDate: Date) => {
    this.displayedDate = endDate
    this.displayedDateRange = {
      start: startDate,
      end: endDate,
    }
  }

  get shouldDisableNextDate() {
    const selectedMonth = getMonth(this.displayedDate)
    const nextMonth = getMonth(addMonths(new Date(), 1))

    return selectedMonth === nextMonth
  }

  get shouldDisablePrevDate() {
    const selectedMonth = getMonth(this.displayedDate)
    const currentMonth = getMonth(new Date())

    return selectedMonth === currentMonth
  }
}
